import { Button } from "@mui/material";
import { FC, useContext } from "react";
import { Control, useFormContext } from "react-hook-form";
import { Trans } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { InputPasswordMui } from "@ryde/controls";
import { useOtpLogin } from "@ryde/redux";
import {
  ASTRIX_SYMBOL,
  I18N_PUBLIC_NAMESPACE,
  LoginAuthType,
  loginFlowStepAtom,
  LoginFormFlow,
  loginParamsAtom,
  onActivateKeyDown,
} from "@ryde/shared";

import { LoginPageFormContext } from "../login.page.form.context";
import { textboxLoginSx } from "../login.page.styled";
import { useLoginTranslation } from "../use-login-translation";
import { LoginAuthButtons } from "./login-auth-buttons";
import { LoginTypeInput } from "./login-type-input";
import { LoginTypeSwitch } from "./login-type-switch";
import { getButtonLoginSx, getLoginTextButtonSx } from "./login.parts.styled";
import { SECOND_TAB_INDEX } from "./tabIndex";

export const LoginGenericForm: FC = () => {
  const { control, trigger } = useFormContext();
  const loginParams = useRecoilValue(loginParamsAtom);
  const { t } = useLoginTranslation();
  const {
    buttonLoginColor,
    isNonProductionLoginColors,
    loginTypeWatchedValue,
    handleSubmit,
  } = useContext(LoginPageFormContext);
  const setLoginFlowStep = useSetRecoilState(loginFlowStepAtom);
  const { requestOtp } = useOtpLogin();

  const isLoginAuthButton = Boolean(
    loginParams?.has(LoginAuthType.Microsoft) ||
    loginParams?.has(LoginAuthType.Google) ||
    loginParams?.has(LoginAuthType.SSO),
  );

  const onLoginOtp = async (): Promise<void> => {
    const isValid = await trigger("Phone");

    if (!isValid) {
      return;
    }

    const isRequestSuccessful = await requestOtp();

    if (!isRequestSuccessful) {
      return;
    }

    setLoginFlowStep(LoginFormFlow.OTP_STEP);
  };

  const onForgotPassword = (): void => {
    setLoginFlowStep(LoginFormFlow.FORGET_PASSWORD);
  };

  return (
    <>
      {isLoginAuthButton && <LoginAuthButtons />}
      <div className="LoginGenericForm">
        <div className="LoginGenericForm-LabelSignIn">
          {t("ui.SIGN_IN_WITH_USERNAME_PHONE_EMAIL")}
        </div>

        <div className="LoginGenericForm-FieldContainer">
          <LoginTypeSwitch name="loginType" />
          <LoginTypeInput loginType={loginTypeWatchedValue} /> {/*ADD TAB*/}
        </div>
        <div className="LoginGenericForm-FieldContainer">
          <div className="LoginGenericForm-FieldContainerLabels">
            <span>
              {t("ui.PASSWORD")}{" "}
              <span className="LoginGenericForm-FieldContainerLabelsAstrix">
                {ASTRIX_SYMBOL}
              </span>
            </span>
            <span
              onClick={onForgotPassword}
              onKeyDown={onActivateKeyDown(onForgotPassword)}
              role="button"
              tabIndex={0}
              className="LoginGenericForm-ForgotPasswordLabel"
            >
              <span className="LoginGenericForm-FieldContainerLabelsAstrix">
                {ASTRIX_SYMBOL}
              </span>{" "}
              {t("ui.FORGOT_PASSWORD")}
            </span>
          </div>
          <InputPasswordMui
            control={control as unknown as Control}
            name="password"
            formLabel={t("ui.PASSWORD")}
            e2eData={"password_login_input"}
            tabIndex={SECOND_TAB_INDEX}
            sx={textboxLoginSx}
          />
        </div>
        <Button
          data-e2e="button-login"
          variant="contained"
          color={isNonProductionLoginColors ? "inherit" : buttonLoginColor}
          sx={getButtonLoginSx(
            isNonProductionLoginColors,
            loginTypeWatchedValue,
          )}
          onClick={handleSubmit}
        >
          {t("ui.LOGIN")}
        </Button>

        {loginTypeWatchedValue === LoginAuthType.Phone && (
          <Button
            data-e2e="button-login-otp"
            variant="text"
            sx={getLoginTextButtonSx(isNonProductionLoginColors)}
            onClick={onLoginOtp}
          >
            {t("login.LOGIN_OTP_BUTTON")}
          </Button>
        )}
        <p className="policyText">
          <Trans
            ns={I18N_PUBLIC_NAMESPACE}
            i18nKey="ui.POLICY_TERMS"
            components={{
              terms: (
                <a rel="noreferrer" target="_blank" href="/">
                  Terms of Service
                </a>
              ),
              privacy: (
                <a rel="noreferrer" target="_blank" href="/">
                  Privacy Policy
                </a>
              ),
            }}
          />
        </p>
      </div>
    </>
  );
};
